import{characterDNAForFighter,type QuantumVacuumGenome}from'./characterDNA'
import type{CharacterPhenotype}from'./characterPhenotype'
import type{Combatant,Intent}from'./simulation'

export type QuantumVacuumTraversalTuning={
 enabled:boolean
 cost:number
 blinkDistance:number
 phaseDuration:number
 cooldown:number
 phaseSpeed:number
 coupling:number
 coherence:number
 stability:number
 extraction:number
 phaseControl:number
 entropyTolerance:number
 shielding:number
 perception:number
}

export type QuantumVacuumRuntimeProfile=QuantumVacuumTraversalTuning & {
 readiness:number
 instability:number
 decoherenceRisk:number
 sensorySignature:number
 usableEnergy:number
}

const clamp=(v:number,lo=0,hi=1)=>Math.max(lo,Math.min(hi,Number.isFinite(v)?v:lo))
const lerp=(a:number,b:number,t:number)=>a+(b-a)*clamp(t)
const avg=(...values:number[])=>values.length?values.reduce((sum,value)=>sum+value,0)/values.length:0

const LEGACY={
 cost:22,
 blinkDistance:1.35,
 phaseDuration:.65,
 cooldown:10,
 phaseSpeed:1.28,
} as const

function boundedGenome(profile:QuantumVacuumGenome){
 return{
  coupling:clamp(profile.coupling),
  coherence:clamp(profile.coherence),
  stability:clamp(profile.stability),
  extraction:clamp(profile.extraction),
  phaseControl:clamp(profile.phaseControl),
  entropyTolerance:clamp(profile.entropyTolerance),
  shielding:clamp(profile.shielding),
  perception:clamp(profile.perception),
 }
}

export function quantumVacuumTraversalTuningFor(fighterId:string):QuantumVacuumTraversalTuning{
 const dna=characterDNAForFighter(fighterId),q=boundedGenome(dna.power.quantumVacuum),enabled=dna.capabilities.quantum
 if(!enabled)return{enabled:false,...LEGACY,...q}
 const mastery=clamp(
  q.coupling*.14+q.coherence*.17+q.stability*.16+q.extraction*.1+
  q.phaseControl*.2+q.entropyTolerance*.08+q.shielding*.06+q.perception*.09
 )
 const efficiency=clamp(q.extraction*.45+dna.physiology.energyEfficiency*.25+dna.power.recovery*.18+q.stability*.12)
 const phase=clamp(q.phaseControl*.46+q.coherence*.3+dna.power.control*.14+dna.power.precision*.1)
 return{
  enabled:true,
  cost:Math.round(lerp(24,18,efficiency)*100)/100,
  blinkDistance:Math.round(lerp(1.25,1.72,phase)*1000)/1000,
  phaseDuration:Math.round(lerp(.56,.82,clamp(q.coherence*.54+q.stability*.3+q.shielding*.16))*1000)/1000,
  cooldown:Math.round(lerp(10.8,7.6,clamp(dna.power.recovery*.38+q.stability*.34+q.extraction*.28))*100)/100,
  phaseSpeed:Math.round(lerp(1.2,1.43,clamp(q.phaseControl*.52+q.coherence*.3+mastery*.18))*1000)/1000,
  ...q,
 }
}

export function quantumVacuumRuntimeProfile(
 fighterId:string,
 phenotype:CharacterPhenotype,
 combatant:Combatant,
):QuantumVacuumRuntimeProfile{
 const tuning=quantumVacuumTraversalTuningFor(fighterId)
 const dna=characterDNAForFighter(fighterId)
 const stamina=clamp(combatant.stamina/100),cooldownReady=clamp(1-(combatant.quantumCooldown??0)/Math.max(.001,tuning.cooldown))
 const mind=phenotype.cognitiveExpression,body=clamp(
  phenotype.neuralIntegrity*.28+phenotype.consciousness*.2+phenotype.motorExpression.balance*.16+
  phenotype.powerExpression.control*.2+phenotype.sensoryExpression.energetic*.16
 )
 const usableEnergy=clamp(stamina*.36+phenotype.sensoryExpression.energetic*.2+phenotype.powerExpression.recovery*.18+tuning.extraction*.26)
 const stability=clamp(tuning.stability*.42+tuning.coherence*.22+mind.cognitiveStability*.16+mind.executiveCapacity*.1+body*.1)
 const instability=clamp(
  dna.power.volatility*.38+(1-stability)*.34+(1-mind.executiveCapacity)*.12+
  phenotype.painLoad*.08+phenotype.fatigueLoad*.08
 )
 const decoherenceRisk=clamp(instability*(1-tuning.entropyTolerance*.5)*(1-tuning.shielding*.35))
 const readiness=tuning.enabled?clamp(
  phenotype.quantumReadiness*.34+usableEnergy*.2+stability*.18+
  tuning.phaseControl*.12+mind.temporalReasoning*.08+mind.sensorFusion*.08
 )*cooldownReady:0
 const sensorySignature=clamp(
  tuning.perception*.36+tuning.coupling*.24+phenotype.sensoryExpression.electromagnetic*.18+
  phenotype.sensoryExpression.energetic*.14+mind.sensorFusion*.08
 )
 return{...tuning,readiness,instability,decoherenceRisk,sensorySignature,usableEnergy}
}

export function applyQuantumVacuumTuning(combatant:Combatant,fighterId:string):Combatant{
 const tuning=quantumVacuumTraversalTuningFor(fighterId)
 if(!tuning.enabled)return{
  ...combatant,
  quantumCost:undefined,quantumBlinkDistance:undefined,quantumPhaseDuration:undefined,
  quantumCooldownBase:undefined,quantumPhaseSpeed:undefined,
  quantumVacuumCoupling:0,quantumVacuumStability:0,
 }
 return{
  ...combatant,
  quantumCost:tuning.cost,
  quantumBlinkDistance:tuning.blinkDistance,
  quantumPhaseDuration:tuning.phaseDuration,
  quantumCooldownBase:tuning.cooldown,
  quantumPhaseSpeed:tuning.phaseSpeed,
  quantumVacuumCoupling:tuning.coupling,
  quantumVacuumStability:tuning.stability,
 }
}

export function admitQuantumCapability(intent:Intent,fighterId:string):Intent{
 if(!intent.quantum)return intent
 return characterDNAForFighter(fighterId).capabilities.quantum?intent:{...intent,quantum:false}
}

export function quantumVacuumTelemetry(fighterId:string){
 const dna=characterDNAForFighter(fighterId),tuning=quantumVacuumTraversalTuningFor(fighterId)
 return{
  fighterId,
  dnaFingerprint:dna.fingerprint,
  capability:dna.capabilities.quantum,
  source:dna.power.source,
  vacuum:{...dna.power.quantumVacuum},
  traversal:tuning,
  invariants:{
   capabilityRequired:true,
   legacyDefaultsPreserved:true,
   noFreeEnergyClaim:true,
   fictionalGameMechanic:true,
  },
 }
}
